import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Trophy,
  Calendar,
  MapPin,
  TrendingUp,
  Star,
  Users,
  Video,
  CheckCircle,
  ArrowRight,
  ExternalLink,
  Clapperboard,
  Image as ImageIcon,
  ChevronRight,
  ChevronDown,
  PlayCircle,
  Award,
  Target,
  DollarSign,
} from "lucide-react";
import woodFloorImage from "@/assets/wood-floor.jpg";
import girlsCircuitHero from "@/assets/girls-circuit-hero.jpg";
import circuitActionPhoto from "@/assets/dsc00568.jpg";
import girlsCircuitCourtBg from "@/assets/girls-circuit-court-bg.jpg";
import leagueFormatDunk from "@/assets/league-format-dunk.jpg";
import leagueFeesPhoto from "@/assets/league-fees-photo.png";
import eventFormatPhoto from "@/assets/event-format-photo.jpg";
import openRunsPhoto from "@/assets/open-runs-photo.jpg";
import larryHughesEliteCamp from "@/assets/larry-hughes-elite-camp.jpg";
import collegeCoachesMay3 from "@/assets/college_coaches_may3_websize.jpg";
import collegeCoachesMay7 from "@/assets/college_coaches_may7_websize_1.jpg";
import collegeCoachesOSL from "@/assets/OSL-3368_websize.jpg";
import collegeCoachesKC from "@/assets/colleges_kc_july1_websize.jpg";
import collegeCoachesChicago from "@/assets/colleges_chicago4_websize.jpg";
import marquetteLogo from "@/assets/marquette-logo.png";
import cjRivers from "@/assets/cj-rivers.jpg";
import druryLogo from "@/assets/drury-logo.png";
import chrisFoster from "@/assets/chris-foster.jpg";
import calStateFullertonLogo from "@/assets/cal-state-fullerton-logo.png";
import ryanHamm from "@/assets/ryan-hamm.jpg";
import iowaLogo from "@/assets/iowa-logo.png";
import connorWheeler from "@/assets/connor-wheeler.jpg";
import alumniCollage from "@/assets/alumni-collage.png";
import alumniBackground from "@/assets/alumni-background.jpg";
import awardOne from "@/assets/award-one.jpg";
import awardTwo from "@/assets/award-two.jpg";
import awardThree from "@/assets/award-three.jpg";
import awardFour from "@/assets/award-four.jpg";
import awardFive from "@/assets/award-five.jpg";
import awardSix from "@/assets/award-six.jpg";
import awardSeven from "@/assets/award-seven.jpg";
import awardEight from "@/assets/award-eight.jpg";
import eventFormatNew from "@/assets/event-format-new.png";
import bellaHines from "@/assets/bella-hines.jpg";
import kamyaYoung from "@/assets/kamya-young.jpg";
import rlHoopsGirlsWhyUs from "@/assets/rl-hoops-girls-why-us.jpg";
import rlHoopsGirlsSchedule from "@/assets/rl-hoops-girls-schedule.jpg";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { UpcomingEvents } from "@/components/hoops/UpcomingEvents";

export default function HoopsGirlsCircuit() {
  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src={girlsCircuitHero}
            alt="RecruitLook Hoops Girls Circuit basketball court"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl sm:text-3xl lg:text-6xl font-black mb-4 leading-tight text-center text-secondary">
            The <span className="text-white">#1</span> Independent Girls Hoops Circuit!
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl mb-10 text-center mx-auto">
            Join the premier girls basketball circuit with elite competition, college coach exposure, and national media coverage. The best play here!
          </p>

          <div className="flex justify-center">
            <Button 
              asChild
              className="bg-secondary hover:bg-secondary/90 text-black font-black px-6 md:px-8 py-3 md:py-4 text-sm tracking-wider"
            >
              <a href="https://form.jotform.com/252124825226149" target="_blank" rel="noopener noreferrer">
                GIRLS CIRCUIT APPLICATION
              </a>
            </Button>
          </div>
        </div>
      </section>


      {/* Upcoming Events */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-secondary text-black px-4 py-2 font-black mb-4">UPCOMING EVENTS</Badge>
            <h2 className="text-3xl lg:text-5xl font-black mb-4">Girls Circuit Events</h2>
            <p className="text-xl text-gray-600">Join us at exclusive basketball tournaments, camps, and showcases</p>
          </div>
          <UpcomingEvents sportSlug="basketball" sportName="basketball" gender="Women" />
        </div>
      </section>

      {/* Quick info cards */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6 max-w-4xl">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3 text-secondary"><Users className="w-5 h-5" /><span className="font-black">9u–17u Teams</span></div>
            <p className="text-gray-700">Competitive divisions, elite venues, national media coverage, and real recruiting exposure.</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3 text-secondary"><Calendar className="w-5 h-5" /><span className="font-black">Non-Live Period Events</span></div>
            <p className="text-gray-700">The tournament series will focus on professionally run Non-Live Period events this year.</p>
          </div>
        </div>
      </section>

      {/* College Coaches */}
      <section className="py-16 bg-gradient-to-br from-primary to-primary/90 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Two Graphics Side by Side */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl overflow-hidden">
              <img 
                src={rlHoopsGirlsWhyUs} 
                alt="RecruitLook Girls - Coverage, Competition, Exposure" 
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img 
                src={rlHoopsGirlsSchedule} 
                alt="RecruitLook Girls Events Schedule" 
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-secondary text-black px-4 py-2 font-black mb-4">HOW IT WORKS</Badge>
            <h2 className="text-3xl lg:text-5xl font-black">How Does It Work?</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="relative bg-white border border-slate-200 rounded-2xl p-6 text-center">
              <div className="flex justify-center mb-4">
                <PlayCircle className="w-12 h-12 text-secondary" />
              </div>
              <h3 className="text-xl font-black mb-2">Play In Events</h3>
              <p className="text-gray-700">Teams can play in one or all of the events. No long-term contracts.</p>
              <ChevronRight className="hidden lg:block absolute -right-5 top-1/2 -translate-y-1/2 w-8 h-8 text-secondary" />
              <ChevronDown className="block lg:hidden mx-auto mt-4 w-8 h-8 text-secondary" />
            </div>
            <div className="relative bg-white border border-slate-200 rounded-2xl p-6 text-center">
              <div className="flex justify-center mb-4">
                <DollarSign className="w-12 h-12 text-secondary" />
              </div>
              <h3 className="text-xl font-black mb-2">Get Discounts</h3>
              <p className="text-gray-700">Verbally commit to more than 1 event & get $100 off per team.</p>
              <ChevronRight className="hidden lg:block absolute -right-5 top-1/2 -translate-y-1/2 w-8 h-8 text-secondary" />
              <ChevronDown className="block lg:hidden mx-auto mt-4 w-8 h-8 text-secondary" />
            </div>
            <div className="relative bg-white border border-slate-200 rounded-2xl p-6 text-center">
              <div className="flex justify-center mb-4">
                <Target className="w-12 h-12 text-secondary" />
              </div>
              <h3 className="text-xl font-black mb-2">July Tournament</h3>
              <p className="text-gray-700">We'll finish the 4 events with our July event, featuring pool play followed by a bracket.</p>
              <ChevronRight className="hidden lg:block absolute -right-5 top-1/2 -translate-y-1/2 w-8 h-8 text-secondary" />
              <ChevronDown className="block lg:hidden mx-auto mt-4 w-8 h-8 text-secondary" />
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center">
              <div className="flex justify-center mb-4">
                <Award className="w-12 h-12 text-secondary" />
              </div>
              <h3 className="text-xl font-black mb-2">Elite Exposure</h3>
              <p className="text-gray-700">Top performers will earn invites to elite camps and showcases.</p>
            </div>
          </div>
        </div>
      </section>

      {/* About the circuit */}
      <section className="py-16 bg-gradient-to-br from-slate-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src={girlsCircuitCourtBg}
            alt="Basketball court background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-1 gap-8">
            <div>
              <div className="mb-8">
                <Badge className="bg-secondary text-black px-4 py-2 font-black mb-4">ABOUT THE CIRCUIT</Badge>
                <h3 className="text-3xl font-black mb-4 text-white">Girls Circuit Information</h3>
                <div className="space-y-4 text-gray-200 leading-relaxed max-w-4xl">
                  <p>The RecruitLook Girls Tournament Series is designed to provide top-tier competition and exposure for female basketball players looking to compete at the next level.</p>
                  <p>The Events are open to any team. Teams will compete in a competitive event landscape, gaining more recruiting exposure through the RecruitLook Media engine!</p>
                  <p>Top players from the events will be recognized on RecruitLook's social media through player graphics and highlight videos. We will also be inviting college coaches to attend these events.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-1 gap-8">
            <div>

              <div className="grid lg:grid-cols-2 gap-8 items-stretch mb-8">
                <div className="bg-secondary text-slate-900 rounded-2xl p-6 h-full flex flex-col justify-center">
                  <h4 className="text-2xl font-black mb-3 flex items-center gap-2 text-slate-900"><Calendar className="w-5 h-5 text-slate-900" /> Event Format</h4>
                  <ul className="list-disc pl-6 space-y-2 text-slate-900">
                    <li>April & May events will be showcase format. The event schedules will be mapped out for the entire weekend for all age groups.</li>
                    <li>The July event will feature a tournament. We'll have a select number of pool games followed by bracket play.</li>
                    <li>These events are open to any and all teams. Teams will enter their team ratings during checkout for balanced pools.</li>
                  </ul>
                  
                  <h4 className="text-2xl font-black mt-6 mb-3 flex items-center gap-2 text-slate-900"><Star className="w-5 h-5 text-slate-900" /> Event Fees</h4>
                  <ul className="list-disc pl-6 space-y-2 text-slate-900">
                    <li>Prices are per event. Verbally commit to play in more than 1 event and receive $100 off per team!</li>
                    <li>9u through 14u = $400/team</li>
                    <li>15u through 17u = $600/team</li>
                    <li>$25 off per team for 2+ teams</li>
                  </ul>
                </div>
                <div className="rounded-2xl overflow-hidden h-full">
                  <img src={bellaHines} alt="Bella Hines basketball player" className="w-full h-full object-cover" />
                </div>
              </div>

              <div className="grid lg:grid-cols-2 gap-8 items-stretch mb-8">
                <div className="rounded-2xl overflow-hidden h-full">
                  <img src={kamyaYoung} alt="Kamya Young basketball player spotlight" className="w-full h-full object-cover" />
                </div>
                <div className="bg-slate-900 text-white rounded-2xl p-6 h-full flex flex-col justify-center">
                  <h4 className="text-2xl font-black mb-3 flex items-center gap-2 text-white"><MapPin className="w-5 h-5 text-white" /> Open Runs</h4>
                  <div className="space-y-3">
                    <p>All events are also Open Runs. We don't want to limit the number of teams that are looking for quality events to play in, so we will offer Open Run Division for teams to come and play at the same venues. These events will be showcase-style for the Open Run Division.</p>
                    <h5 className="text-lg font-black">First Class</h5>
                    <p>We want to make this a great experience for everyone involved. We want you to be proud of the Circuit that you are in. We know that the best advertising and marketing is word-of-mouth, and we'll strive to make a lasting impression on you. Our team has been running events for decades, and we know that attention to detail, big or small, is key when running an event. We'll make sure that your time on the RecruitLook Hoops Circuit is enjoyable.</p>
                  </div>
                </div>
              </div>


              <div className="mb-4 text-center">
                <h4 className="text-2xl font-black mb-3 flex items-center gap-2 justify-center"><Video className="w-5 h-5 text-secondary" /> Live Streaming</h4>
                <p className="text-gray-700 mb-4">ALL RecruitLook Hoops Events will be Live Streamed through BallerTV for College Coaches and Fans to enjoy!</p>
                <div className="flex justify-center">
                  <Button asChild variant="outline" className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-black">
                    <a href="https://www.ballertv.com/organizations/recruitlook" target="_blank" rel="noopener noreferrer">Visit BallerTV.com/RecruitLook</a>
                  </Button>
                </div>
              </div>
              <div className="flex justify-center my-6">
                <div className="h-1 bg-slate-900 w-3/4 rounded-full"></div>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Media coverage examples */}
      <section className="pt-8 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-secondary text-black px-4 py-2 font-black mb-4">MEDIA COVERAGE</Badge>
            <h3 className="text-3xl lg:text-5xl font-black">Media Coverage Examples</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 text-secondary font-black mb-2"><Star className="w-5 h-5" /> Recruiting Coverage</div>
              <p className="text-gray-700">A quick way to highlight a player that has performed well. We'll tweet before, during, and after events to promote players.</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 text-secondary font-black mb-2"><Clapperboard className="w-5 h-5" /> Video Highlights</div>
              <p className="text-gray-700">Our media team is filming games, editing clips, and pushing out highlight videos during the event for more exposure.</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-2 text-secondary font-black mb-2"><ImageIcon className="w-5 h-5" /> Event Pics</div>
              <p className="text-gray-700">Our media team took pictures of all the teams that competed in RecruitLook Hoops events that were available for download and print.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Apply Now CTA */}
      <section className="py-16 bg-gradient-to-br from-primary to-primary/90 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-5xl font-black mb-6">Ready to Join the Girls Circuit?</h2>
          <p className="text-xl text-white/90 mb-8">Apply now to be part of the premier girls basketball circuit with elite competition and national exposure.</p>
          <Button 
            asChild
            className="bg-secondary hover:bg-secondary/90 text-black font-black px-8 py-4 text-lg"
          >
            <a href="https://form.jotform.com/252124825226149" target="_blank" rel="noopener noreferrer">
              Apply for Girls Circuit
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
}

function StatCard({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-secondary text-black rounded-2xl p-6 text-center shadow">
      <div className="text-xl sm:text-2xl lg:text-4xl font-black mb-2">{value}</div>
      <div className="text-xs sm:text-sm lg:text-base text-black font-semibold">{label}</div>
    </div>
  );
}
